import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core'
import { Router } from '@angular/router'
import { NavService } from './nav.service'
import { NavItem } from '@shared/nav-list-item.component'
import { Store, select } from '@ngrx/store'
import { AppState } from './core/store'
import { RoleGuard } from './guards/role-guard.service'
import { selectAuthState } from './core/store/auth/auth.selectors'
import { Subscription } from 'rxjs'
import { State } from './core/store/auth/auth.state.facade'
import { environment } from 'src/environments/environment'
import { LOCKTON_COM, ACTUARIAL_UI_ROUTE } from './core/model/auth.model'
import { MatNavList } from '@angular/material/list'
import { UserRoles } from './admin/model/admin.model'

import { NavListItemComponent } from './shared/nav-list-item.component'

@Component({
  selector: 'app-nav-list-container',
  styleUrls: ['./nav-list.container.scss'],
  template: `
    <mat-nav-list disableRipple>
      @for (item of visibleItems; track item) {
        <app-nav-list-item
          [item]="item"
          [expandedMap]="expandedMap"
          (itemSelect)="onItemSelect($event)"
        />
      }
    </mat-nav-list>
  `,
  imports: [MatNavList, NavListItemComponent],
})
export class NavListContainerComponent implements OnInit, OnDestroy {
  router = inject(Router)
  private navService = inject(NavService)
  private cd = inject(ChangeDetectorRef)
  private store = inject<Store<AppState>>(Store)
  private roleGuard = inject(RoleGuard)

  expandedMap: Record<string, boolean> = {}
  allowedGuards: Set<string>
  navSubscription: Subscription
  hasFacultativeSecurityPermission = false
  hasActuarialUiAccess = false

  get visibleItems(): NavItem[] {
    return this.items.filter((item) => this.isItemVisible(item))
  }

  ngOnInit() {
    this.navSubscription = this.navService.currentUrl.subscribe(() => {
      this.cd.markForCheck()
      this.store.pipe(select(selectAuthState)).subscribe((data) => {
        this.hasFacultativeSecurityPermission = this.hasFacultativeSecurity(data)
        this.hasActuarialUiAccess = this.hasActuarialUi(data)

        if (data.userPermissions && data.security) {
          this.allowedGuards = this.roleGuard.checkPermissions(data.userPermissions, data.security)
          this.items.map((item) => {
            if (item.displayName === 'FAC SAGE') {
              item.disabled = !this.hasFacultativeSecurityPermission
              return
            }
            item.disabled = item.route ? this.checkDisabled(item.route) : false
            if (item.children) {
              item.children.map((child) => {
                child.disabled = child.route ? this.checkDisabled(child.route) : false
              })
              if (this.allChildrenItemsDisabled(item)) {
                item.disabled = true
              }
            }
          })
        }
      })
      this.store.pipe(select(selectAuthState)).subscribe((data) => {
        this.hasFacultativeSecurityPermission = this.hasFacultativeSecurity(data)
        this.hasActuarialUiAccess = this.hasActuarialUi(data)

        /* To protect against refreshes */
        const selectedApp = this.getSelectedApp(data)

        this.items.map((item) => (item.disabled = this.disableNavigationItem(item, selectedApp)))
      })
    })
    this.expandedMap['SAGE Structuring Tool'] = false
    this.expandedMap.Group = true
  }

  getSelectedApp(data: State): string {
    if (data.selectedApp && data.selectedApp !== '') {
      return data.selectedApp
    }

    const isFac = this.router.url === '/facultative' || this.router.url === '/facultative-admin'

    return isFac ? 'facultative' : 'sage'
  }

  disableNavigationItem(item: NavItem, selectedApp: string): boolean | undefined {
    if (item.displayName === 'FAC SAGE') {
      return !this.hasFacultativeSecurityPermission
    }

    if (item.route === ACTUARIAL_UI_ROUTE) {
      return !this.hasActuarialUiAccess
    }

    if (
      selectedApp === 'facultative' &&
      item.displayName !== 'Facultative' &&
      item.displayName !== 'Facultative Administration'
    ) {
      return true
    }

    if (
      selectedApp === 'sage' &&
      (item.displayName === 'Facultative' || item.displayName === 'Facultative Administration')
    ) {
      return true
    }

    if (
      selectedApp === 'sage' &&
      item.displayName !== 'Facultative' &&
      item.displayName !== 'Facultative Administration'
    ) {
      return item.disabled
    }

    return false
  }

  private hasFacultativeSecurity(data: State): boolean {
    return data.security?.some((authSec) => authSec === 'Facultative') ?? false
  }

  private hasActuarialUi(data: State): boolean {
    return (
      data.sageLogicalRole === UserRoles.Actuary &&
      (data.username?.toLowerCase().endsWith(`@${LOCKTON_COM}`) ?? false)
    )
  }

  private isItemVisible(item: NavItem): boolean {
    if (item.displayName === 'FAC SAGE') {
      return this.hasFacultativeSecurityPermission
    }

    if (item.route === ACTUARIAL_UI_ROUTE) {
      return this.hasActuarialUiAccess
    }

    return true
  }

  getKey(item: NavItem): string {
    return item.route || item.displayName
  }

  onItemSelect(item: NavItem): void {
    if (item.isExternal) {
      window.open(item.externalUrl, '_blank')
    } else if (!item.children || !item.children.length) {
      this.router.navigate([item.route])
      this.navService.close()
    } else {
      const _expanded = this.expandedMap[this.getKey(item)]
      this.expandedMap[this.getKey(item)] = !_expanded
    }
  }

  checkDisabled(route: string): boolean {
    if (route === ACTUARIAL_UI_ROUTE) {
      return !this.hasActuarialUiAccess
    }
    if (this.allowedGuards) {
      return !this.allowedGuards.has(route)
    }
  }

  ngOnDestroy() {
    this.navSubscription.unsubscribe()
  }

  items: NavItem[] = [
    {
      displayName: 'Program Initiation',
      iconName: 'create_new_folder',
      route: '/program-initiation',
      disabled: false,
    },
    {
      displayName: 'Reinsurer Selection',
      iconName: 'work',
      route: '/reinsurers',
      disabled: false,
    },
    {
      displayName: 'SAGE Structuring Tool',
      iconName: 'bar_chart',
      home: true,
      children: [
        { displayName: 'Pricing Curve', route: '/pricingcurve' },
        { displayName: 'Design', route: '/home' },
        {
          displayName: 'Group',
          route: '/analysis/group',
          disabled: false,
        },
        {
          displayName: 'Compare',
          route: '/analysis/compare',
          disabled: false,
        },
        {
          displayName: 'Metrics',
          route: '/metrics',
          disabled: false,
        },
      ],
    },
    {
      displayName: 'Request for Quote',
      iconName: 'send',
      route: '/submit-to-market',
      disabled: false,
    },
    {
      displayName: 'Slip Templates',
      iconName: 'book',
      route: '/sliptemplates',
      disabled: false,
    },
    {
      displayName: 'Quote SAGE',
      iconName: 'request_quote',
      children: [
        { displayName: 'Quotes', route: '/quote/main', disabled: false },
        {
          displayName: 'Dashboards',
          route: '/quote/management-information',
          disabled: false,
        },
        // {
        //   displayName: 'Data Ingestion',
        //   route: '/quote/gpt',
        //   disabled: this.isProd,
        // },
        {
          displayName: 'AI Quote Ingestion',
          route: '/quote/ai-quote-ingestion',
          disabled: false,
        },
      ],
    },
    {
      displayName: 'Signature Page',
      iconName: 'content_paste',
      route: '/signature',
      disabled: true,
    },
    {
      displayName: 'Tracking',
      iconName: 'table_chart',
      route: '/tracking',
      disabled: false,
    },
    {
      displayName: 'CAT Modeling Workspace',
      iconName: 'storage',
      route: '/cat-modeling-workspace',
      disabled: false,
    },
    {
      displayName: 'Benchmarking',
      iconName: 'show_chart',
      route: '/benchmark',
      disabled: false,
    },
    {
      displayName: 'PROVE',
      iconName: 'map',
      route: '/prove',
      disabled: false,
    },
    {
      displayName: 'Admin Dashboard',
      iconName: 'lock',
      children: [{ displayName: 'SAGE', route: '/admin/main', disabled: false }],
    },
    {
      displayName: 'KPI Dashboard',
      iconName: 'dashboard',
      route: '/kpi-dashboard',
      disabled: false,
    },
    {
      displayName: 'Management Information',
      iconName: 'dashboard',
      route: '/management-information',
      disabled: false,
    },
    {
      displayName: 'MI Dashboard',
      iconName: 'dashboard',
      route: '/mi-dashboard',
      disabled: false,
    },
    {
      displayName: 'Revenue Dashboard',
      iconName: 'dashboard',
      route: '/revenue-dashboard',
      disabled: false,
    },
    {
      displayName: 'Actuarial UI',
      iconName: 'format_shapes',
      route: ACTUARIAL_UI_ROUTE,
      disabled: true,
      externalUrl: environment.actuarialUiURI.base,
      isExternal: true,
    },
    {
      displayName: 'Demo',
      iconName: 'play_circle',
      route: '/demo',
      disabled: false,
    },
    {
      displayName: 'FAC SAGE',
      iconName: 'open_in_new',
      route: '/fac-redirect',
      disabled: false,
    },
    // Currently Not in use
    // {
    //   displayName: 'Explore',
    //   iconName: 'search',
    //   route: '/home',
    //   disabled: false,
    // },
  ]

  allChildrenItemsDisabled(item: NavItem): boolean {
    const foundEnabled = item.children?.some((c) => !c.disabled)

    return !foundEnabled
  }
}
